import { Badge } from "@thilke/ui/components/badge";
import { Panel } from "@thilke/ui/components/panel";
import { Stat, StatLabel, StatValue } from "@thilke/ui/components/ui/stat";
import { cn } from "@thilke/ui/lib/utils";
import type * as React from "react";

export interface ItemProperty {
  label: string;
  value: React.ReactNode;
}

export interface ItemSheetProps extends React.ComponentProps<"div"> {
  name: string;
  itemType?: string;
  rarity?: string;
  icon?: React.ReactNode;
  properties?: ItemProperty[];
  footer?: React.ReactNode;
}

function ItemSheet({
  name,
  itemType = "Item",
  rarity,
  icon,
  properties = [],
  footer,
  children,
  className,
  ...props
}: ItemSheetProps) {
  return (
    <Panel
      position="docked"
      size="md"
      className={cn("grid gap-3", className)}
      {...props}
    >
      <header className="flex items-start gap-3">
        <div className="flex size-10 items-center justify-center rounded-md border border-border bg-background text-xs text-muted-foreground">
          {icon ?? itemType.slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <h2 className="truncate text-sm font-semibold leading-tight">
              {name}
            </h2>
            <Badge variant="outline">{itemType}</Badge>
            {rarity ? <Badge variant="accent">{rarity}</Badge> : null}
          </div>
          {properties.length > 0 ? (
            <dl className="mt-2 grid grid-cols-2 gap-1.5 text-xs">
              {properties.map((property) => (
                <Stat
                  key={property.label}
                  className="rounded-md p-2.5 shadow-none"
                >
                  <StatLabel className="text-xs uppercase tracking-wide">
                    {property.label}
                  </StatLabel>
                  <StatValue className="font-mono text-sm">
                    {property.value}
                  </StatValue>
                </Stat>
              ))}
            </dl>
          ) : null}
        </div>
      </header>
      {children ? (
        <div className="rounded-md border border-border bg-card p-3 text-xs leading-normal text-card-foreground">
          {children}
        </div>
      ) : null}
      {footer ? (
        <footer className="border-t border-border pt-2 text-xs text-muted-foreground">
          {footer}
        </footer>
      ) : null}
    </Panel>
  );
}

export { ItemSheet };
